import { Controller } from "@hotwired/stimulus"

/**
 * Ad Click Tracker Controller
 *
 * Records ad card clicks for performance stats (no billing)
 * Uses Navigator.sendBeacon for reliable tracking during page navigation
 *
 * Features:
 * - Client-side debounce (30 seconds via sessionStorage)
 * - Fire-and-forget beacon (non-blocking)
 * - CSRF token handling for Rails security
 * - Graceful degradation for private browsing
 *
 * Server-side also implements IP-based deduplication as backup
 *
 * Usage:
 *   <a href="/ads/1"
 *      data-controller="ad-click-tracker"
 *      data-action="click->ad-click-tracker#track"
 *      data-ad-click-tracker-url-value="/ads/1/track_click"
 *      data-ad-click-tracker-ad-id-value="1">
 *     Ad Card Content
 *   </a>
 */
export default class extends Controller {
  static values = {
    url: String,            // POST /ads/:id/track_click endpoint
    adId: Number,           // Ad ID for client-side deduplication key
    debounceMs: { type: Number, default: 30000 }  // Debounce window in milliseconds (default: 30s)
  }

  connect() {
    // Session-based click tracking key
    // Format: "ad_clicked_{adId}" with timestamp value
    this._clickedKey = `ad_clicked_${this.adIdValue}`
  }

  /**
   * Track click event
   * Called via data-action="click->ad-click-tracker#track"
   *
   * Important: Does NOT call preventDefault() - allows normal navigation
   */
  track(_event) {
    // 1. Client-side debounce: skip if clicked within last 30 seconds
    if (this._isRecentlyClicked()) {
      return // Navigation continues, tracking skipped
    }

    // 2. Mark as clicked in sessionStorage
    this._markAsClicked()

    // 3. Send tracking beacon (fire-and-forget)
    this._sendBeacon()

    // 4. Normal navigation continues (no preventDefault)
  }

  /**
   * Check if this ad was clicked recently (within 30 seconds)
   * Uses sessionStorage for per-tab isolation
   *
   * @returns {boolean} true if recent click exists
   */
  _isRecentlyClicked() {
    try {
      const timestamp = sessionStorage.getItem(this._clickedKey)
      if (!timestamp) return false

      const elapsed = Date.now() - parseInt(timestamp, 10)
      return elapsed < this.debounceMsValue // Debounce window from data attribute
    } catch {
      // sessionStorage may be unavailable (private browsing, quota exceeded)
      // Allow tracking in this case (server handles deduplication)
      return false
    }
  }

  /**
   * Mark this ad as clicked with current timestamp
   */
  _markAsClicked() {
    try {
      sessionStorage.setItem(this._clickedKey, Date.now().toString())
    } catch {
      // Silent fail - tracking still works without client-side deduplication
      // Server-side IP+time check serves as backup
    }
  }

  /**
   * Send tracking beacon to server
   * Uses Navigator.sendBeacon for reliability during page navigation
   *
   * sendBeacon is preferred because:
   * - Guaranteed delivery even during page unload
   * - Non-blocking (doesn't delay navigation)
   * - Browser queues and sends asynchronously
   */
  _sendBeacon() {
    // Get CSRF token from meta tag (Rails security requirement)
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

    if (!csrfToken) {
      // This shouldn't happen in normal Rails app
      // Skip tracking rather than send insecure request
      return
    }

    // Use FormData for sendBeacon (automatic Content-Type handling)
    // sendBeacon doesn't support custom headers, so we use FormData
    const formData = new FormData()
    formData.append("authenticity_token", csrfToken)

    try {
      navigator.sendBeacon(this.urlValue, formData)
      // Note: sendBeacon returns boolean but we don't need to handle failure
      // Server logs will show if tracking is working
    } catch {
      // sendBeacon failure is non-critical
      // User experience (navigation) takes priority over tracking
    }
  }
}
